import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FastProject } from "../../../types/interfaces";
import { useAppDispatch } from "../../../app/hooks";
import { clearProject } from "../../../app/features/project/projectSlice";
import useDeleteProject from "../useDeleteProject";
import useRemoveMeFromProject from "./useRemoveMeFromProject";
import useFinalizeProject from "./useArchiveProject";
import useEditTags from "./useEditTags";
import useCopyProject from "./useCopyProject";
import useExportProject from "./useExportProject";

// this hook is used to handle the actions of the projects
interface UseProjectActionsReturn {
  // Delete
  isDeleteDialogOpen: boolean;
  handleDelete: () => void;
  handleRestore: (id: number) => void;
  handleCancel: () => void;
  isLoadingDelete: boolean;
  deleteProjectErrorMsg: string;
  isDeleteDialogErrorMessageOpen: boolean;
  closeDeleteDialogErrorMessage: () => void;
  // Remove
  isRemoveDialogOpen: boolean;
  handleRemove: () => void;
  handleCancelRemove: () => void;
  removeMeFromProjectErrorMsg: string;
  isRemoveMeFromProjectLoading: boolean;
  isRemoveMeFromProjectErrorMessageOpen: boolean;
  closeRemoveMeFromProjectErrorMessage: () => void;
  // Finalize
  isFinalizeDialogOpen: boolean;
  handleCancelFinalize: () => void;
  handleFinalize: () => void;
  isLoadingFinalize: boolean;
  finalizeProjectErrorMsg: string;
  isFinalizeDialogErrorMessageOpen: boolean;
  closeFinalizeProjectErrorMessage: () => void;
  // Tags
  isEditTagsModalOpen: boolean;
  closeEditTagsModal: () => void;
  projectToEditTags: FastProject | null;
  handleSubmitTags: (initialTags: any[], newTags: any[]) => void;
  isEditTagsDialogErrorMessageOpen: boolean;
  closeEditTagsDialogErrorMessage: () => void;
  editTagsErrorMsg: string;
  // Copy
  isLoadingCopy: boolean;
  copyProjectErrorMsg: string;
  isCopyProjectErrorMessageOpen: boolean;
  closeCopyProjectErrorMessage: () => void;
  isDuplicateWarningOpen: boolean;
  handleConfirmDuplicate: () => Promise<void>;
  handleCancelDuplicate: () => void;
  // Export
  isExportWarningOpen: boolean;
  handleConfirmExport: () => void;
  handleCancelExport: () => void;
  // Actions
  handleNewProject: () => void;
  handleDuplicateProject: (project: FastProject) => Promise<void>;
  handleEditProject: (project: FastProject) => void;
  handleExportProject: (project: FastProject) => void;
  showDeleteModal: (project: FastProject) => void;
  showRemoveModal: (project: FastProject) => void;
  showFinalizeModal: (project: FastProject) => void;
  showEditTagsModal: (project: FastProject) => void;
}

export const useProjectActions = (userId: number, refetch: () => void): UseProjectActionsReturn => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { showDeleteModal,
    isDeleteDialogOpen,
    handleDelete,
    handleCancel,
    handleRestore,
    isLoadingDelete,
    deleteProjectErrorMsg,
    isDeleteDialogErrorMessageOpen,
    closeDeleteDialogErrorMessage } =
    useDeleteProject(refetch);

  const {
    showRemoveModal,
    isRemoveDialogOpen,
    handleRemove,
    handleCancel: handleCancelRemove,
    removeMeFromProjectErrorMsg,
    isRemoveMeFromProjectLoading,
    isRemoveMeFromProjectErrorMessageOpen,
    closeRemoveMeFromProjectErrorMessage,
  } = useRemoveMeFromProject(userId, refetch);

  const {
    isFinalizeDialogOpen,
    handleCancel: handleCancelFinalize,
    handleFinalize,
    showFinalizeModal,
    isLoadingFinalize,
    finalizeProjectErrorMsg,
    isFinalizeDialogErrorMessageOpen,
    closeFinalizeProjectErrorMessage,
  } = useFinalizeProject(refetch);

  const {
    isEditTagsModalOpen,
    showEditTagsModal,
    closeEditTagsModal,
    projectToEditTags,
    handleSubmitTags,
    isEditTagsDialogErrorMessageOpen,
    closeEditTagsDialogErrorMessage,
    error: editTagsErrorMsg
  } = useEditTags(refetch);

  const {
    handleDuplicateProject,
    isLoadingCopy,
    copyProjectErrorMsg,
    isCopyProjectErrorMessageOpen,
    closeCopyProjectErrorMessage,
    isDuplicateWarningOpen,
    handleConfirmDuplicate,
    handleCancelDuplicate,
  } = useCopyProject(refetch);

  const {
    handleExportProject,
    isExportWarningOpen,
    handleConfirmExport,
    handleCancelExport,
  } = useExportProject();

  const handleNewProject = useCallback(() => {
    dispatch(clearProject());
    navigate("/project/new/description");
  }, [dispatch, navigate]);

  const handleEditProject = useCallback((project: FastProject) => {
    if (!project?.id) {
      console.warn('No project ID found for editing');
      return;
    }
    navigate(`/project/${project.id}/description`);
  }, [navigate]);

  return {
    // Delete
    isDeleteDialogOpen,
    handleDelete,
    handleRestore,
    handleCancel,
    isLoadingDelete,
    deleteProjectErrorMsg,
    isDeleteDialogErrorMessageOpen,
    closeDeleteDialogErrorMessage,
    // Remove
    isRemoveDialogOpen,
    handleRemove,
    handleCancelRemove,
    removeMeFromProjectErrorMsg,
    isRemoveMeFromProjectLoading,
    isRemoveMeFromProjectErrorMessageOpen,
    closeRemoveMeFromProjectErrorMessage,
    // Finalize
    isFinalizeDialogOpen,
    handleCancelFinalize,
    handleFinalize,
    isLoadingFinalize,
    finalizeProjectErrorMsg,
    isFinalizeDialogErrorMessageOpen,
    closeFinalizeProjectErrorMessage,
    // Tags
    isEditTagsModalOpen,
    closeEditTagsModal,
    projectToEditTags,
    handleSubmitTags,
    isEditTagsDialogErrorMessageOpen,
    closeEditTagsDialogErrorMessage,
    editTagsErrorMsg,
    // Copy
    isLoadingCopy,
    copyProjectErrorMsg,
    isCopyProjectErrorMessageOpen,
    closeCopyProjectErrorMessage,
    isDuplicateWarningOpen,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    // Export
    isExportWarningOpen,
    handleConfirmExport,
    handleCancelExport,
    // Actions
    handleNewProject,
    handleDuplicateProject,
    handleEditProject,
    handleExportProject,
    showDeleteModal,
    showRemoveModal,
    showFinalizeModal,
    showEditTagsModal,
  };
}; 